import { Dealer } from "@/types/dealer";
import { CACHE_EXPIRATION } from "./services/cacheConfig";
import { isSSR } from "@/lib/isSSR";

// Define a global interface to hold the cache
declare global {
  interface Window {
    __DEALER_CACHE__: {
      [key: string]: {
        timestamp: number;
        data: Dealer[];
      };
    } | undefined;
    __PREFETCHED_DEALERS__?: {
      metal: string;
      timestamp: number;
      data: Dealer[];
    };
    // Add the cache version property to the Window interface
    __CACHE_VERSION?: string;
  }
}

// Initialize the cache in the global scope if it doesn't exist
if (typeof window !== 'undefined') {
  window.__DEALER_CACHE__ = window.__DEALER_CACHE__ || {};
}

/**
 * Get recommended dealers data from cache
 * @param key Cache key
 * @returns Cached dealers data if available and not expired, otherwise null
 */
export const getCachedRecommendedDealers = (key: string): Dealer[] | null => {
  if (typeof window === 'undefined' || !window.__DEALER_CACHE__) {
    return null;
  }
  
  const cachedData = window.__DEALER_CACHE__[key];
  
  if (cachedData && Date.now() - cachedData.timestamp < CACHE_EXPIRATION) {
    console.log(`[Cache] Returning cached data for key: ${key}`);
    return cachedData.data;
  }
  
  return null;
};

/**
 * Cache recommended dealers data
 * @param key Cache key
 * @param data Dealers data to cache
 */
export const cacheRecommendedDealers = (key: string, data: Dealer[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  
  window.__DEALER_CACHE__ = window.__DEALER_CACHE__ || {};
  window.__DEALER_CACHE__[key] = {
    timestamp: Date.now(),
    data: data,
  };
  
  console.log(`[Cache] Cached data for key: ${key}`);
};

/**
 * Store prefetched dealers for immediate use
 * @param metal Metal type (e.g., "ORO", "ARGENTO")
 * @param dealers Dealer data to prefetch
 */
export const setPrefetchedDealers = (metal: string, dealers: Dealer[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  window.__PREFETCHED_DEALERS__ = {
    metal,
    timestamp: Date.now(),
    data: dealers
  };
  
  console.log(`[Cache] Prefetched ${dealers.length} dealers for ${metal}`);
};

/**
 * Get prefetched dealers from cache
 * @returns Prefetched dealers or null if none exist or expired
 */
export const getPrefetchedDealersFromCache = (): Dealer[] | null => {
  if (typeof window === 'undefined' || !window.__PREFETCHED_DEALERS__) {
    return null;
  }
  
  const prefetchedData = window.__PREFETCHED_DEALERS__;
  const PREFETCH_TTL = 300000; // 5 minutes
  
  if (Date.now() - prefetchedData.timestamp > PREFETCH_TTL) {
    // Clear expired prefetch data
    window.__PREFETCHED_DEALERS__ = undefined;
    return null;
  }
  
  return prefetchedData.data;
};

/**
 * Check if the cache version is outdated and clear the cache if necessary
 */
export const checkCacheVersion = () => {
  // Default cache version
  const DEFAULT_CACHE_VERSION = '1';
  
  // Get cache version safely without using import.meta.env
  // This works in both client and server environments
  const currentVersion = !isSSR() && 
                        typeof window !== 'undefined' && 
                        typeof window.document !== 'undefined' && 
                        // Try to get from a global variable if defined
                        (window.__CACHE_VERSION || DEFAULT_CACHE_VERSION);
                        
  const storedVersion = typeof localStorage !== 'undefined' ? localStorage.getItem('cacheVersion') : null;
  
  if (storedVersion !== currentVersion) {
    console.log('Cache version outdated. Clearing cache.');
    
    // Clear all dealer-related caches
    invalidateAllDealerCaches();
    
    // Update the stored cache version
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('cacheVersion', currentVersion as string);
    }
  }
};

/**
 * Invalidate all dealer-related caches to force fresh data
 */
export const invalidateAllDealerCaches = () => {
  console.log("Invalidating all dealer caches to force fresh data");
  
  // Clear localStorage cache for all metals
  try {
    const metals = ["ORO", "ARGENTO"];
    
    metals.forEach(metal => {
      // Clear localStorage cache
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(`recommended-dealers-data-${metal}`);
        localStorage.removeItem(`recommended-dealers-timestamp-${metal}`);
        localStorage.removeItem(`fallback-dealers-${metal}`);
        localStorage.removeItem(`fallback-dealers-timestamp-${metal}`);
      }
      
      // Clear in-memory cache
      if (typeof window !== 'undefined' && window.__DEALER_CACHE__) {
        delete window.__DEALER_CACHE__[`recommended-dealers-data-${metal}`];
      }
    });

    // Clear prefetched dealers
    if (typeof window !== 'undefined') {
      window.__PREFETCHED_DEALERS__ = undefined;
    }
    
  } catch (error) {
    console.error("Error invalidating dealer caches:", error);
  }
  
  console.log("All dealer caches have been invalidated");
};

// Add a global cache version property for client-side access
if (typeof window !== 'undefined') {
  window.__CACHE_VERSION = '1';
}
